import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, getStandupsGroupedByDate, signOut, refreshSlackWorkspaceName } from '../lib/supabase'
import { Tables, TeamRole as TeamRoleType } from '../types/supabase'
import VoiceRecorder from '../components/VoiceRecorder'
import NewDashboard from '../components/NewDashboard'
import { OnboardingFlow } from '../components/OnboardingFlow'
import { useAuth } from '../contexts/AuthContext'
import SkeletonLoader from '../components/SkeletonLoader'
import { Button } from '../components/ui/button'
import { SidebarProvider, SidebarInset } from '../components/ui/sidebar'
import { AppSidebar, DashboardSection } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { BillingManagement } from '../components/BillingManagement'
import { getSubscriptionStatus, checkTrialStatus } from '../lib/billing'
import { AlertCircle, Crown, X } from 'lucide-react'

// Type aliases for better readability
type Team = Tables<'teams'>
type StandupResponse = Tables<'standup_responses'>
// Was a stale local 'admin' | 'member' shadowing the real TeamRole
// ('admin' | 'member' | 'manager', src/types/supabase.ts) -- fixed
// 2026-09-29 alongside the pod-manager permissions slice (see
// docs/ALERTING_PERMISSIONS_PLAN.md). This page still doesn't branch on
// 'manager' explicitly: the only cost-sensitive widget it renders
// (AgentsCostPanel, via NewDashboard) already self-gates on role and is
// RLS-scoped to the manager's own pods; everything else here is standup
// roll-call content, which isn't restricted by pod for any role. Audited,
// no further scoping needed -- see PodStandupOverrides.tsx/TeamCheckins.tsx
// for where manager-scoping actually does something on this dashboard.
type TeamRole = TeamRoleType

interface DashboardStats {
  totalStandups: number
  thisWeekStandups: number
  activeUsers: number
  teamHealthScore: number
  standupCompletionRate: number
  voiceMemos?: number
  avgSentiment?: number
  voicePercentage?: number
}

interface TeamDashboardProps {
  teamId: string;
}

interface ExtendedStandupResponse {
  id: string
  date: string
  user: string
  user_id: string
  sentiment: number
  audio_path: string | null
  questions: {
    yesterday: string
    today: string
    blockers: string
  }
}

interface GroupedStandupDay {
  date: string
  standups: Array<{
    name: string
    avatar?: string
    timestamp: string
    completed: string
    planned: string
    blockers?: string
    sentiment?: 'positive' | 'neutral' | 'negative'
    sentimentNumeric?: number
    isVoiceEntry?: boolean
    rawDate?: string
    rawCreatedAt?: string
  }>
}

const TeamDashboard: React.FC<TeamDashboardProps> = ({ teamId }) => {
  const navigate = useNavigate()
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const { refreshUserTeams } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Dashboard data
  const [stats, setStats] = useState<DashboardStats>({
    totalStandups: 0,
    thisWeekStandups: 0,
    activeUsers: 0,
    teamHealthScore: 0,
    standupCompletionRate: 0,
    voiceMemos: 0,
    avgSentiment: 0,
  })
  const [groupedStandups, setGroupedStandups] = useState<GroupedStandupDay[]>([])
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false)
  const [teamMemberCount, setTeamMemberCount] = useState<number>(1)
  
  const [userRole, setUserRole] = useState<TeamRole>('member')


  // Dashboard shell state (sidebar nav)
  const [activeSection, setActiveSection] = useState<DashboardSection>('dashboard')

  const handleSectionNavigate = (section: DashboardSection) => {
    setActiveSection(section)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Billing/trial state
  const [showBilling, setShowBilling] = useState(false)
  const [trialStatus, setTrialStatus] = useState<{
    isTrialExpired: boolean
    isOnTrial: boolean
    daysRemaining: number
    trialEndDate?: Date
  } | null>(null)
  const [showTrialBanner, setShowTrialBanner] = useState(true)

  // Debug effect to log trial status
  useEffect(() => {
    if (trialStatus) {
      console.log('🎯 Trial Status Updated:', {
        isExpired: trialStatus.isTrialExpired,
        isOnTrial: trialStatus.isOnTrial,
        daysRemaining: trialStatus.daysRemaining,
        showBanner: showTrialBanner,
        shouldShowBanner: trialStatus.isTrialExpired || (trialStatus.isOnTrial && trialStatus.daysRemaining <= 3)
      })
    }
  }, [trialStatus, showTrialBanner])

  useEffect(() => {
    const loadTeamData = async () => {
      try {
        console.log('=== TEAM DASHBOARD LOADING ===')
        setLoading(true);
        setError(null);

        const { data: team, error: teamError } = await supabase
          .from('teams')
          .select('id, name, slack_team_id, created_at, standup_config, app_uninstalled_at, plan')
          .eq('id', teamId)
          .single()

        if (teamError) throw new Error(`Failed to load team: ${teamError.message}`)
        if (!team) throw new Error('Team not found')

        setSelectedTeam(team as Team)

        // Sub-pages (Summary, Check-ins, Agents, ...) don't have their own
        // Billing UI -- their sidebar calls here with ?open=billing to open
        // that panel once, then this cleans up the URL the same way the
        // billing-success redirect below does.
        //
        // ?open=team-settings used to do the same for a Team Settings modal
        // here (TeamSettings.tsx) -- retired in favor of navigating straight
        // to the real /team-management page, which already has the current
        // design system and is a strict superset of what that modal did
        // (members + billing + schedule, across TeamManagement.tsx /
        // TeamBilling.tsx / TeamConfig.tsx). Every sidebar call site was
        // updated to link there directly; this case only remains so an old
        // bookmarked/shared ?open=team-settings URL still goes somewhere
        // sensible instead of silently doing nothing.
        const openParam = new URLSearchParams(window.location.search).get('open')
        if (openParam === 'team-settings') {
          navigate(`/dashboard/team/${teamId}/team-management`, { replace: true })
          return
        }
        if (openParam === 'billing') {
          setShowBilling(true)
          const newUrl = window.location.pathname + window.location.hash
          window.history.replaceState({}, '', newUrl)
        }

        // Check trial status
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (user) {
            console.log('📊 Checking trial status for team:', teamId)
            const subscriptionStatus = await getSubscriptionStatus(teamId, user.id)
            console.log('📊 Subscription status:', subscriptionStatus)
            const trial = checkTrialStatus(subscriptionStatus, team.created_at || undefined)
            console.log('📊 Trial status result:', trial)
            console.log('📊 Trial expired?', trial.isTrialExpired)
            console.log('📊 Days remaining:', trial.daysRemaining)
            setTrialStatus(trial)
          }
        } catch (error) {
          console.error('❌ Error checking trial status:', error)
        }
        
        setUserRole('member')
        
        // Get current user's role in the team
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          const { data: teamMember } = await supabase
            .from('team_members')
            .select('role')
            .eq('team_id', teamId)
            .eq('auth_user_id', user.id)
            .single()
          
          if (teamMember && teamMember.role) {
            setUserRole(teamMember.role as TeamRole)
          }

        }

        await loadTeamDashboardData(team as Team)
      } catch (err) {
        console.error('Dashboard error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [teamId]);

  useEffect(() => {
    if (!selectedTeam?.slack_team_id) return
    let cancelled = false
    let inFlight = false
    let lastAttempt = 0
    const syncName = async () => {
      if (inFlight || Date.now() - lastAttempt < 60_000) return
      inFlight = true
      lastAttempt = Date.now()
      try {
        const name = await refreshSlackWorkspaceName(selectedTeam.slack_team_id)
        if (cancelled || !name) return
        if (name !== selectedTeam.name) {
          setSelectedTeam(current => current?.id === selectedTeam.id ? { ...current, name } : current)
          await refreshUserTeams?.()
        }
      } catch { /* Keep the existing name during Slack outages. */ }
      finally { inFlight = false }
    }
    void syncName()
    window.addEventListener('focus', syncName)
    return () => { cancelled = true; window.removeEventListener('focus', syncName) }
  }, [selectedTeam?.id, selectedTeam?.slack_team_id, selectedTeam?.name, refreshUserTeams])

  const loadTeamDashboardData = async (team: Team) => {
    try {
      // Get team members count
      const { data: teamMembers, error: teamMembersError } = await supabase
        .from('team_members')
        .select('*')
        .eq('team_id', team.id)
      
      if (teamMembersError) {
        console.error('Error loading team members:', teamMembersError)
      } else {
        setTeamMemberCount(teamMembers?.length || 1)
      }

      // Get grouped standups
      const groupedStandupData = await getStandupsGroupedByDate(team.id, 14)
      
      // Calculate stats from all standups
      const allStandups = groupedStandupData.flatMap(([date, standups]) => standups)
      const totalStandups = allStandups.length
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
      const thisWeekStandups = allStandups.filter(s => s.created_at && new Date(s.created_at) >= oneWeekAgo).length
      const voiceMemos = allStandups.filter(s => s.audio_path).length
      
      // Calculate actual average sentiment
      const standupsWithSentiment = allStandups.filter(s => s.sentiment !== null && s.sentiment !== undefined)
      const avgSentiment = standupsWithSentiment.length > 0 
        ? standupsWithSentiment.reduce((sum, s) => sum + (s.sentiment || 0), 0) / standupsWithSentiment.length
        : 0
      
      setStats(prevStats => ({
        ...prevStats,
        totalStandups,
        thisWeekStandups,
        voiceMemos,
        avgSentiment,
        voicePercentage: totalStandups > 0 ? (voiceMemos / totalStandups) * 100 : 0
      }))
      
      // Format grouped standups
      const formattedGroupedStandups: GroupedStandupDay[] = groupedStandupData.map(([date, standups]) => {
        const getSentimentDisplay = (sentiment: number): 'positive' | 'neutral' | 'negative' => {
          if (sentiment > 0.2) return 'positive'
          if (sentiment < -0.2) return 'negative'
          return 'neutral'
        }
        
        const formattedStandups = standups.map(standup => {
          // console.log('=== FORMATTING STANDUP DEBUG ===')
          // console.log('Raw standup data:', {
          //   date: standup.date,
          //   created_at: standup.created_at,
          //   id: standup.id,
          //   sentiment: standup.sentiment
          // })
          
          return {
            name: standup.users?.name || 'Unknown User',
            timestamp: new Date(standup.date).toLocaleDateString(),
            completed: (standup.questions as any)?.yesterday || 'N/A',
            planned: (standup.questions as any)?.today || 'N/A',
            blockers: (standup.questions as any)?.blockers && (standup.questions as any)?.blockers !== 'None' ? (standup.questions as any)?.blockers : undefined,
            sentiment: getSentimentDisplay(standup.sentiment || 0),
            sentimentNumeric: standup.sentiment || 0, // Preserve numeric sentiment for mood calculation
            isVoiceEntry: !!standup.audio_path,
            // Add debug info for the date
            rawDate: standup.date,
            rawCreatedAt: standup.created_at || undefined
          }
        })
        
        return {
          date,
          standups: formattedStandups,
        }
      })
      
      setGroupedStandups(formattedGroupedStandups)
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      throw error
    }
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const handleVoiceStandupSubmitted = () => {
    setShowVoiceRecorder(false)
    if (selectedTeam) {
      loadTeamDashboardData(selectedTeam)
    }
  }

  const handleTeamClick = () => {
    navigate(`/dashboard/team/${teamId}/team-management`)
  }

  if (loading) {
    // Real sidebar/topbar chrome, skeleton only for the part that's actually
    // still loading (team data) — keeps the loading state visually identical
    // to the real layout instead of a separately-maintained mockup.
    return (
      <SidebarProvider>
        <AppSidebar
          activeSection={activeSection}
          onNavigate={handleSectionNavigate}
          onOpenTeamManagement={handleTeamClick}
          onOpenBilling={() => setShowBilling(true)}
          teamId={teamId}
        />
        <SidebarInset>
          <TopBar teamId={teamId} onLogout={handleLogout} />
          <main className="bg-background p-4 sm:p-6">
            <SkeletonLoader />
          </main>
        </SidebarInset>
      </SidebarProvider>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center"><p>Error: {error}</p></div>
    )
  }

  if (!selectedTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center"><p>Team not found.</p></div>
    )
  }
  
  // Main dashboard view
  return (
    <SidebarProvider>
      <AppSidebar
        activeSection={activeSection}
        onNavigate={handleSectionNavigate}
        onOpenTeamManagement={handleTeamClick}
        onOpenBilling={() => setShowBilling(true)}
        teamId={selectedTeam.id}
      />

      <SidebarInset>
        <TopBar
          teamId={selectedTeam.id}
          teamName={selectedTeam.name || undefined}
          onLogout={handleLogout}
          onUpgrade={() => setShowBilling(true)}
          showUpgrade={trialStatus?.isOnTrial || false}
          upgradeUrgent={trialStatus?.isTrialExpired || false}
        />

      {/* Trial Status Banner - Prominent and Clear */}
      {trialStatus && showTrialBanner && (trialStatus.isTrialExpired || (trialStatus.isOnTrial && trialStatus.daysRemaining <= 3)) && (
        <div className={`relative z-20 border-b ${trialStatus.isTrialExpired ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
          <div className="max-w-[1800px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                <div className="flex-shrink-0">
                  {trialStatus.isTrialExpired ? (
                    <AlertCircle className={`h-8 w-8 ${trialStatus.isTrialExpired ? 'text-red-600' : 'text-amber-600'}`} />
                  ) : (
                    <Crown className="h-8 w-8 text-amber-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold">
                    {trialStatus.isTrialExpired 
                      ? '⏰ Your Trial Has Expired' 
                      : `⚠️ Trial Ends in ${trialStatus.daysRemaining} Day${trialStatus.daysRemaining === 1 ? '' : 's'}`}
                  </h3>
                  <p className="text-sm opacity-80">
                    {trialStatus.isTrialExpired 
                      ? 'Upgrade now to continue submitting standups and keep your team synced.'
                      : 'Add a payment method now to avoid any interruption to your standups.'}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Button
                    onClick={() => setShowBilling(true)}
                    className="gap-2"
                  >
                    <Crown className="!size-5" />
                    Upgrade Plan
                  </Button>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowTrialBanner(false)}
                className="ml-4 size-11 shrink-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
                aria-label="Dismiss banner"
              >
                <X className="!size-5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Management Modal */}
      {showBilling && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-auto border border-border bg-background shadow-lg">
            <div className="sticky top-0 flex items-center justify-between border-b border-border bg-background px-6 py-4">
              <h2 className="text-2xl font-bold text-foreground">Upgrade Your Plan</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowBilling(false)}
                className="size-11 text-muted-foreground hover:bg-transparent hover:text-foreground"
              >
                <X className="!size-6" />
              </Button>
            </div>
            <div className="p-6">
              <BillingManagement 
                teamId={selectedTeam.id}
                userRole={userRole}
                isOpen={showBilling}
                onClose={() => setShowBilling(false)}
              />
            </div>
          </div>
        </div>
      )}

      <main className="space-y-6 bg-background p-4 sm:p-6">
        <OnboardingFlow teamId={teamId} teamName={selectedTeam.name || 'your workspace'} role={userRole}
          standupsEnabled={(selectedTeam.standup_config as Record<string, unknown> | null)?.enabled === true || (selectedTeam.standup_config as Record<string, unknown> | null)?.digest_enabled === true} />
        <NewDashboard
          teamId={teamId}
          teamName={selectedTeam.name || undefined}
          standupData={groupedStandups.flatMap(day => day.standups.map(standup => ({
            user_name: standup.name,
            completed_work: standup.completed,
            planned_work: standup.planned,
            blockers: standup.blockers,
            created_at: standup.rawCreatedAt || standup.rawDate, // Use the raw date from database
            sentiment: standup.sentimentNumeric // Use the numeric sentiment for mood calculation
          })))}
          teamStats={stats}
          teamMemberCount={teamMemberCount}
        />
      </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default TeamDashboard 
